import * as Sentry from "@sentry/react";
import { History, Maximize2, Minimize2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC
} from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Tag } from "@/components/ui/tag";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

import {
  getDraftDocx,
  getDraftPdf,
  OpenGapsError,
  UnresolvedCitationsError,
  updateDraft
} from "../../../api/drafts-v2";
import { useLegacyDrafting } from "../../../hooks/useLegacyDrafting";
import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import { citationPrefix } from "../../draftCitations/model";
import { StaleCitationsBanner } from "../../draftCitations/StaleCitationsBanner";
import {
  DraftDocxEditor,
  type DraftDocxEditorHandle
} from "../../draftEditor/DraftDocxEditor";
import { useUnsavedDocument } from "../../draftEditor/documentState";
import { downloadDocx } from "../../draftEditor/docxExport";
import { printPdfBytes } from "../../draftEditor/printPdf";
import type {
  DocxEditorMode,
  DocxFileMenuItem,
  DocxSurfaceHandle
} from "../../draftEditor/surface";
import { useReviewerName } from "../../draftReview/components/ReviewModeControl";
import { useDraftReview } from "../../draftReview/useDraftReview";
import { DraftVersionHistory } from "../../draftVersioning/components/DraftVersionHistory";
import { useDraftProvenance } from "../../draftVersioning/useDraftProvenance";
import { DraftAnalysisStrip } from "../../keyFacts/components/DraftAnalysisStrip";
import { NoticeScoreStrip } from "../../keyFacts/components/NoticeScoreStrip";
import { DraftQualityStrip } from "./DraftQualityStrip";
import { DraftRunBanner, revisingSince } from "./DraftRunBanner";
import { useCaseIde, useOnInvalidate } from "../context/CaseIdeContext";
import { useCaseData } from "../data/CaseDataContext";
import { AddToCaseFilesDialog } from "../files/AddToCaseFilesDialog";
import { draftCopySource, type CopySource } from "../files/caseFileCopies";
import { artifactKey } from "../types";
import { DraftCitationLayer } from "./DraftCitationLayer";
import { DraftFailedState, emptyDraftFailure } from "./DraftFailedState";
import { draftStatus, draftTitle } from "../workspace/workspaceModel";
import { ClassicRunPane } from "./ClassicRunPane";
import { ClassicStart } from "./ClassicStart";
import { ClassicStrip } from "./ClassicStrip";
import { classicRunning, isClassicDraft } from "./classicModel";

type Props = {
  draftId: string;
};

/**
 * A draft open in the center pane. Edits go straight back as docx; the agent's
 * own edits arrive through `data-invalidate` and reload the surface. Version
 * history sits in the editor's title bar; the .docx download (for Word) and
 * Print sit in its File menu. Download and print are both server renders, so
 * citations carry the packet's current exhibit numbers however long ago the
 * draft was written.
 */
export const DraftPane: FC<Props> = ({ draftId }) => {
  const {
    setSelectedText,
    invalidate,
    activeArtifact,
    renameTab,
    reveal,
    revealInDraft,
    clearReveal,
    requestPrompt
  } = useCaseIde();
  const { fs, workspace } = useCaseData();
  // A draft with no document yet (queued, waiting on an analysis) has no
  // editor to carry the banner, so the strip sits above the pane instead.
  const listed = workspace.drafts.find((d) => d.draft_id === draftId);
  const empty = !!listed && !listed.docx_storage_path;
  const failed = emptyDraftFailure(listed) !== null;
  // Classic firms choose Casey or Classic for a draft nothing has written yet.
  const legacyDrafting = useLegacyDrafting();
  const [caseyChosen, setCaseyChosen] = useState(false);
  const classicWriting = classicRunning(listed);
  const listedStatus = listed ? draftStatus(listed) : null;
  const showChooser =
    legacyDrafting &&
    empty &&
    !failed &&
    !caseyChosen &&
    listedStatus !== "running" &&
    listedStatus !== "waiting" &&
    listed?.draft_type !== "custom";
  const classic = isClassicDraft(listed);
  // Refetched when the draft's status moves, which is when a run lands.
  const provenance = useDraftProvenance(
    draftId,
    !!listed?.docx_storage_path,
    listedStatus
  );
  const accountName = useSelector(lawyerSelectors.selectFullName);
  // Comments and tracked changes are signed with this; attorneys can pick a
  // different name than the account's (initials, a firm convention).
  const reviewer = useReviewerName(accountName);
  const editorRef = useRef<DraftDocxEditorHandle | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Every draft opens in Editing; a mode picked here lasts for this pane only.
  const [mode, setMode] = useState<DocxEditorMode>("editing");
  const [commentsOpen, setCommentsOpen] = useState(false);
  // Focus mode: the editor over the whole window; Escape brings the pane back.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);
  const [downloading, setDownloading] = useState(false);
  const [promoteSource, setPromoteSource] = useState<CopySource | null>(null);
  const [dirty, setDirty] = useState(false);
  // A run rewriting the draft replaces its bytes on handback, so it opens to
  // read meanwhile. Edits already unsaved stay editable: the save is fenced.
  const revising = revisingSince(listed);
  const runLocked = revising !== null && !dirty;
  // Our own save fans out on the bus too; the editor already holds those bytes.
  const ownSave = useRef(false);
  // Refetch pending changes on every bus hit, including our own saves: an
  // attorney accepting a change in Word and re-uploading changes the count.
  const [reviewKey, setReviewKey] = useState(0);

  // --- Citations and the packet they point at -------------------------------
  //
  // The document holds node ids, never labels; the server writes the current
  // label into each citation on every read. So when exhibits are renumbered
  // while a draft is open, the painted text is stale until the next load. The
  // hover card is never stale (it reads `fs.labels`), but the page is, so:
  // a clean editor reloads at once; a dirty one gets a banner offering to save
  // (the PUT re-resolves too) and reload in one step.
  //
  // `labelBaseline` is what each cited exhibit was labelled when the document
  // was last loaded (seeded from `fs` as citations are painted, reset on every
  // reload), so only a change to an exhibit this draft cites counts.
  const [citedNodeIds, setCitedNodeIds] = useState<string[]>([]);
  const [numberingStale, setNumberingStale] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const labelBaseline = useRef(new Map<string, string | null>());

  // A reload swaps the server's bytes into the running editor; the editor
  // keeps unsaved edits and shows a notice instead when there are any. Before
  // the editor is up there is nothing to swap into, so remount.
  const reload = useCallback(() => {
    labelBaseline.current = new Map();
    setNumberingStale(false);
    const editor = editorRef.current;
    if (editor) {
      editor.reload().catch((error) => {
        Sentry.captureException(error);
      });
    } else {
      setReloadKey((k) => k + 1);
    }
  }, []);

  useOnInvalidate(
    ["drafts", "research"],
    useCallback(() => {
      setReviewKey((k) => k + 1);
      if (ownSave.current) {
        ownSave.current = false;
        return;
      }
      reload();
    }, [reload])
  );

  const prefix = citationPrefix(fs.scheme);
  useEffect(() => {
    if (citedNodeIds.length === 0) return;
    const baseline = labelBaseline.current;
    const current = new Map<string, string | null>([["$prefix", prefix]]);
    for (const id of citedNodeIds) current.set(id, fs.labels?.[id] ?? null);

    let changed = false;
    for (const [key, value] of current) {
      if (!baseline.has(key)) {
        // First sight of this citation: the document was painted with what the
        // filesystem says now.
        baseline.set(key, value);
      } else if (baseline.get(key) !== value) {
        changed = true;
      }
    }
    if (!changed) return;
    if (dirty) {
      setNumberingStale(true);
    } else {
      reload();
    }
  }, [citedNodeIds, fs.labels, prefix, dirty, reload]);

  // Citations to exhibits that have left the packet, as of the filesystem the
  // IDE holds right now. Overrides the count the editor read at load time so
  // an exhibit removed while the draft is open shows up without a reload.
  const liveUnresolved = useMemo(
    () =>
      citedNodeIds.length === 0
        ? undefined
        : citedNodeIds.filter((id) => !fs.labels?.[id]).length,
    [citedNodeIds, fs.labels]
  );

  // After a save, our own bus echo is normally ignored (the editor holds the
  // bytes). Not while the numbering is stale: then the reload is the point,
  // since the PUT stored re-resolved bytes the editor has not seen.
  const onSaved = useCallback(() => {
    if (!numberingStale) ownSave.current = true;
    invalidate(["drafts"]);
  }, [numberingStale, invalidate]);

  const saveAndRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await editorRef.current?.save();
    } catch {
      // The editor shows the save failure in its title bar; nothing to add.
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Tracked changes awaiting review. The editor's own cards are the only
  // review surface: we open that sidebar when new changes arrive (a live
  // propose, or a reload after a server-side tracked edit) and scroll to the
  // first new one. Accept / reject is the attorney's act on those cards.
  const review = useDraftReview({
    draftId,
    refreshKey: reviewKey
  });
  const lastPendingCount = useRef<number | null>(null);
  const seenChangeIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    lastPendingCount.current = null;
    seenChangeIds.current = new Set();
  }, [draftId]);
  useEffect(() => {
    if (review.pendingCount == null) return;
    const count = review.pendingCount;
    const ids = review.openChanges.map((change) => change.id);
    const previous = lastPendingCount.current;
    lastPendingCount.current = count;
    if (count === 0) {
      seenChangeIds.current = new Set();
      return;
    }
    const firstKnown = previous === null;
    const rose = previous !== null && count > previous;
    if (firstKnown || rose) {
      setCommentsOpen(true);
      const nextId = ids.find((id) => !seenChangeIds.current.has(id)) ?? ids[0];
      const revisionId = Number(nextId);
      if (Number.isInteger(revisionId)) {
        requestAnimationFrame(() => {
          editorRef.current?.surface()?.revealChange(revisionId);
        });
      }
    }
    seenChangeIds.current = new Set(ids);
  }, [review.pendingCount, review.openChanges]);

  // "Show in document" from a chat card.
  const paneReveal =
    reveal && reveal.draftId === draftId
      ? { text: reveal.text, changeId: reveal.changeId, nonce: reveal.nonce }
      : null;
  const onRevealed = useCallback(
    (nonce: number, found: boolean) => {
      clearReveal(nonce);
      if (!found) toast.message("That passage isn't in the document anymore.");
    },
    [clearReveal]
  );

  // Selection is scoped to the open tab; clear it when this pane goes away.
  useEffect(() => () => setSelectedText(null), [setSelectedText]);

  // Exhibit citations in the document become live: hover for the exhibit and
  // its cited page, open the file from the card. Stable identity so the editor
  // does not re-render for it.
  const citationOverlay = useCallback(
    (host: HTMLElement | null, surface: DocxSurfaceHandle | null) => (
      <DraftCitationLayer
        host={host}
        surface={surface}
        onCitedNodesChange={setCitedNodeIds}
      />
    ),
    []
  );
  // The version-history preview is a different document; it should not feed
  // the live draft's citation tracking.
  const previewOverlay = useCallback(
    (host: HTMLElement | null, surface: DocxSurfaceHandle | null) => (
      <DraftCitationLayer host={host} surface={surface} />
    ),
    []
  );

  // What downloads is what the attorney sees: unsaved edits are saved first
  // (the PUT also re-resolves exhibit labels), then the server's bytes go out
  // as-is, tracked changes and comments included, for working on in Word.
  const download = useCallback(async () => {
    setDownloading(true);
    try {
      if (editorRef.current?.isDirty()) {
        try {
          await editorRef.current.save();
        } catch {
          // The editor's notice explains (conflict or failure); do not hand
          // out a file that is not what the page shows.
          toast.message("Save your changes before downloading.");
          return;
        }
      }
      const { bytes, filename } = await getDraftDocx(draftId);
      downloadDocx(bytes, filename);
    } finally {
      setDownloading(false);
    }
  }, [draftId]);

  // The copy is rendered from the saved draft, so unsaved edits are saved first.
  const addToCaseFiles = useCallback(async () => {
    if (!listed) return;
    if (editorRef.current?.isDirty()) {
      try {
        await editorRef.current.save();
      } catch {
        toast.message(
          "Save your changes before adding the draft to Case Files."
        );
        return;
      }
    }
    setPromoteSource(draftCopySource(listed));
  }, [listed]);

  // A restored version or an uploaded Word copy lands in the open editor and
  // autosaves like any edit (conflict check included), as a version of its own
  // so the text it replaced stays in history.
  const loadAsNewVersion = useCallback(
    async (bytes: ArrayBuffer, trigger: "restore" | "word_upload") => {
      const editor = editorRef.current;
      if (!editor) throw new Error("The draft editor is not open.");
      await editor.loadUnsaved(bytes, { trigger });
    },
    []
  );
  const restoreVersion = useCallback(
    (bytes: ArrayBuffer) => loadAsNewVersion(bytes, "restore"),
    [loadAsNewVersion]
  );

  // The Word add-in is retired; the round trip for attorneys who still edit
  // in Word is download → edit → upload here.
  const uploadInput = useRef<HTMLInputElement | null>(null);
  const uploadEdited = async (file: File | undefined) => {
    if (!file) return;
    if (!/\.docx$/i.test(file.name)) {
      toast.message("Choose a .docx file saved from Word.");
      return;
    }
    try {
      await loadAsNewVersion(await file.arrayBuffer(), "word_upload");
      toast.message(
        "Loaded your Word copy. The text it replaced stays in version history."
      );
    } catch (error) {
      Sentry.captureException(error);
      toast.message("Couldn't open that file. Is it a Word document?");
    }
  };
  const currentBytes = useCallback(
    () => editorRef.current?.getBuffer() ?? Promise.resolve(null),
    []
  );

  // Print goes through the server's PDF render, never the editor's bytes: the
  // render resolves every citation against the packet as it stands, so a draft
  // written before exhibits were reorganised prints with the new numbers. A
  // citation to an exhibit that has left the packet stops the print until the
  // attorney says so, since the marker would otherwise go to paper unnoticed.
  // So does a blank the drafting agent left open: its comment does not print.
  const print = useCallback(
    async (
      options: { allowUnresolved?: boolean; allowOpenGaps?: boolean } = {}
    ) => {
      try {
        const { bytes } = await getDraftPdf(draftId, options);
        printPdfBytes(bytes);
      } catch (error) {
        if (error instanceof OpenGapsError) {
          const n = error.openGaps;
          toast.warning(
            `${n} blank${n === 1 ? "" : "s"} still need${
              n === 1 ? "s" : ""
            } filling.`,
            {
              description:
                "Each highlighted blank has a comment saying what goes there. Comments don't print, so the blank prints empty.",
              action: {
                label: "Print anyway",
                onClick: () => void print({ ...options, allowOpenGaps: true })
              }
            }
          );
          return;
        }
        if (error instanceof UnresolvedCitationsError) {
          const n = error.unresolvedCitations;
          toast.warning(
            `${n} citation${n === 1 ? "" : "s"} point${n === 1 ? "s" : ""} at ${
              n === 1 ? "an exhibit" : "exhibits"
            } no longer in the packet.`,
            {
              description:
                "The print will show “[exhibit removed]” where the citation was.",
              action: {
                label: "Print anyway",
                onClick: () => void print({ ...options, allowUnresolved: true })
              }
            }
          );
          return;
        }
        Sentry.captureException(error);
        toast.error("Couldn't prepare this draft for printing.");
      }
    },
    [draftId]
  );
  const printNow = useCallback(() => void print(), [print]);

  const title = activeArtifact?.title ?? "Draft";

  // Closing the tab or the window with unsaved edits asks first.
  const isDirty = useCallback(() => editorRef.current?.isDirty() ?? false, []);
  const discardDraft = useCallback(
    () => editorRef.current?.discardRecovery(),
    []
  );
  useUnsavedDocument(
    artifactKey({ kind: "draft", id: draftId }),
    title,
    isDirty,
    discardDraft
  );

  const rename = useCallback(
    async (next: string) => {
      // Optimistic: the tab and workspace tree pick the name up immediately;
      // the refetch below settles it. The editor keeps its bytes (`ownSave`).
      renameTab(artifactKey({ kind: "draft", id: draftId }), next);
      try {
        await updateDraft(draftId, { title: next });
      } catch (error) {
        Sentry.captureException(error);
        toast.error("Couldn't rename this draft.");
      } finally {
        // On failure the refetch also rolls the tab and title field back.
        ownSave.current = true;
        invalidate(["drafts"]);
      }
    },
    [draftId, renameTab, invalidate]
  );

  // Rendered inside the editor's own title bar, next to the save status.
  const titleBarActions = (
    <>
      {classic ? <Tag>Classic</Tag> : null}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground"
            aria-label="Version history"
            onClick={() => setHistoryOpen(true)}
          >
            <History className="size-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Version history</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground"
            aria-label={expanded ? "Restore panel" : "Expand document"}
            data-testid="draft-focus-toggle"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? (
              <Minimize2 className="size-3.5" />
            ) : (
              <Maximize2 className="size-3.5" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {expanded ? "Restore panel (Esc)" : "Expand document"}
        </TooltipContent>
      </Tooltip>
      {/* Opened from File › Upload edited copy. */}
      <input
        ref={uploadInput}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        aria-label="Upload edited .docx"
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Reset so picking the same file again re-fires the change.
          event.target.value = "";
          void uploadEdited(file);
        }}
      />
    </>
  );

  // Ahead of the editor's own File › Print, which `onPrint` routes to the
  // server render above.
  const fileMenuItems = useMemo<DocxFileMenuItem[]>(
    () => [
      {
        label: "Download",
        icon: "file_download",
        disabled: downloading,
        onClick: () => void download()
      },
      {
        label: "Upload edited copy…",
        icon: "file_upload",
        disabled: mode === "viewing",
        onClick: () => uploadInput.current?.click()
      },
      {
        label: "Add to Case Files…",
        icon: "link",
        disabled: !listed?.docx_storage_path,
        onClick: () => void addToCaseFiles()
      }
    ],
    [download, downloading, mode, listed, addToCaseFiles]
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {empty ? (
        <DraftAnalysisStrip
          draftId={draftId}
          onFoldedIn={() => invalidate(["drafts"])}
        />
      ) : null}
      <div className="min-h-0 flex-1">
        {failed && listed ? (
          <DraftFailedState
            draft={listed}
            onRetry={requestPrompt}
            onClassicRetried={() => invalidate(["drafts"])}
          />
        ) : classicWriting ? (
          <ClassicRunPane draft={listed} />
        ) : showChooser && listed ? (
          <ClassicStart
            key={draftId}
            draft={listed}
            onChooseCasey={() => setCaseyChosen(true)}
          />
        ) : (
          <DraftDocxEditor
            key={draftId}
            expanded={expanded}
            ref={editorRef}
            draftId={draftId}
            reloadKey={reloadKey}
            author={reviewer.name}
            mode={mode}
            onModeChange={setMode}
            commentsSidebarOpen={commentsOpen}
            onCommentsSidebarOpenChange={setCommentsOpen}
            title={title}
            onRename={rename}
            readOnly={runLocked}
            titleBarActions={titleBarActions}
            titleNote={provenance}
            unresolvedCitations={liveUnresolved}
            banner={
              <>
                {revising ? (
                  <DraftRunBanner
                    since={revising.since}
                    activity={revising.activity}
                  />
                ) : null}
                {classic && listed && !empty ? (
                  <ClassicStrip
                    draft={listed}
                    onRevise={() =>
                      requestPrompt(`Revise the ${draftTitle(listed)}: `)
                    }
                  />
                ) : null}
                {empty ? null : (
                  <DraftAnalysisStrip
                    draftId={draftId}
                    onFoldedIn={() => invalidate(["drafts"])}
                  />
                )}
                <NoticeScoreStrip
                  draftId={draftId}
                  onShow={(title) => revealInDraft(draftId, title)}
                />
                <DraftQualityStrip draftId={draftId} />
                {numberingStale ? (
                  <StaleCitationsBanner
                    busy={refreshing}
                    onSaveAndRefresh={() => void saveAndRefresh()}
                  />
                ) : null}
              </>
            }
            reveal={paneReveal}
            onRevealed={onRevealed}
            onSelectionChange={(selection) =>
              setSelectedText(selection?.text ?? null)
            }
            onDirtyChange={setDirty}
            onSaved={onSaved}
            autosave
            onPrint={printNow}
            fileMenuItems={fileMenuItems}
            overlay={citationOverlay}
          />
        )}
      </div>
      <DraftVersionHistory
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        draftId={draftId}
        draftTitle={title}
        // The invalidate listener above reloads the editor with the restored bytes.
        onRestored={() => invalidate(["drafts"])}
        editorOverlay={previewOverlay}
        onRestoreUnsaved={restoreVersion}
        currentBytes={currentBytes}
      />
      <AddToCaseFilesDialog
        source={promoteSource}
        onOpenChange={(o) => !o && setPromoteSource(null)}
      />
    </div>
  );
};
